import { useEffect, useSyncExternalStore, type ReactNode } from "react";
import { useRouterState } from "@tanstack/react-router";
import {
  ClipboardList,
  CreditCard,
  LayoutGrid,
  MapPin,
  Monitor,
  Package,
  PackagePlus,
  PackageSearch,
  QrCode,
  Settings,
  ShieldCheck,
  Bell,
  Tag,
  Ticket,
  Truck,
  User,
  Users,
  Wallet,
} from "lucide-react";

import { Crumbs, DesktopShell, SideLayout, SideNavGroup, SideNavItem } from "./shell.tsx";
import { cn } from "@/lib/utils";
import { useIdentities, useMe } from "../../api/queries.ts";
import { onIdentityChanged, readIdentity, writeIdentity } from "../../lib/identity.ts";

/**
 * ═══════════════════════════════════════════════════════════════
 *  网页版 · 账户中心的外壳（左导航常驻）
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **网页版的账户中心不是"一页菜单 + 点进去 + 返回"。**
 *    手机上只能这样：屏幕一次装一件事，所以「我的」是一张菜单，
 *    点一条进二级页，看完按返回。网页上有横向空间 ——
 *    导航常驻在左边，右边换内容，**改完地址接着看订单不用返回两次**。
 *
 * ⚠️ 所有账户/经营页共用这一个外壳，导航是同一份。
 *    每页各写一份的话，加一个入口要改十几个文件，迟早漏掉几页。
 *
 * 🔴 **身份切换也放在这里。**
 *    一个人既是买家又是经营者是常态；导航内容整块跟着身份换 ——
 *    切到「我自己」是订单地址，切到某家店是经营入口。
 *    两套堆在一起的话左栏会长到要自己滚动。
 */

export function AccountShell({
  title,
  crumb,
  children,
}: {
  /** 右侧内容区的标题。传空串表示这一页自己画标题。 */
  title?: string;
  /** 面包屑最后一节的文字，默认跟 title 一样。 */
  crumb?: string;
  children: ReactNode;
}) {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const { data: me } = useMe();
  const { data: identities } = useIdentities(Boolean(me));

  /**
   * 🔴 **必须订阅身份变更事件，不能只在挂载时读一次。**
   *    切身份的唯一入口在**顶栏账号卡**（这个侧栏已经不放切换器了）——
   *    它写的是 localStorage，而 localStorage 不会让 React 重渲染。
   *    只读一次的话：在顶栏切成卖家，左边这张菜单还停在买家那一套，
   *    要刷新一次才跟上，看着像切换没生效。
   *
   * ⚠️ 用自发的 `zx:identity-changed` 事件，不能用浏览器的 `storage` 事件 ——
   *    后者只在**别的标签页**改动时触发，同一个页面自己写是收不到的。
   */
  const identity = useSyncExternalStore(onIdentityChanged, readIdentity, () => null);
  const setIdentity = (v: string) => writeIdentity(v);

  const orgs = identities?.orgs ?? [];
  const activeOrg = orgs.find((o) => String(o.id) === identity) ?? null;

  /** 记住的那个组织可能已经不在了（被清退、被移出成员）—— 对不上就退回「我自己」。 */
  useEffect(() => {
    if (identities && identity && !activeOrg) setIdentity("");
  }, [identity, identities, activeOrg]);

  const at = (p: string) => path === p;
  const orgSearch = activeOrg ? { orgId: activeOrg.id } : undefined;

  /**
   * ═══════════════════════════════════════════════════════════════
   *  能干什么，按服务端的实际放行范围来
   * ═══════════════════════════════════════════════════════════════
   *
   * 🔴 **导航里出现一个点进去必然报错的入口，比没有这个入口更糟。**
   *    这几档权限在 `apps/api/src/routes/merchant.ts` 里是**三套不同的名单**，
   *    早先前端只按"是不是经销/分销/平台"粗分了一刀 —— 于是平台身份的菜单里
   *    挂着「收益明细」「提现」「团队与客户」「我的商品」「店铺设置」五个入口，
   *    每一个点进去都是一句红色的「这个操作只对 supplier / dealer / distributor 开放」。
   *
   * ⚠️ 改这里的时候要对着那个文件看，别凭印象：
   *    - `pricerOnly`    = platform / dealer / distributor —— 定价、货架、发券
   *    - `inviterOnly`   = platform / dealer / distributor —— 邀请客户
   *    - `resellerOnly`  = dealer / distributor            —— 团队与客户
   *    - `supplierOnly`  = supplier / dealer / distributor —— 我自己上架的商品
   *    - `anyMerchant`   = supplier / dealer / distributor —— 店铺设置、余额、提现
   *      （平台的钱和店在 `/admin` 平台后台里看，不走商家端这套。）
   */
  const type = activeOrg?.type;
  /** 能定价、能摆货架、能发券。 */
  const canPrice = type === "platform" || type === "dealer" || type === "distributor";
  /** 有下级和客户可管（平台的组织树在后台看）。 */
  const canRecruit = type === "dealer" || type === "distributor";
  /**
   * 谁能上架自己的货。
   *
   * 🔴 **经销商不在里面**（老板 2026-08-31 定的）：经销商是签约渠道商，
   *    卖的是链上游供的货、靠差价吃饭，不自己组货；分销商（装修师傅那一档）
   *    会有自己进的辅料和自己做的定制柜，所以他能上架。
   *
   * ⚠️ 这份名单必须跟服务端 `merchant.ts` 的 `supplierOnly` 一字不差 ——
   *    多放一个，菜单里就会挂着一个点进去必然 403 的入口；少放一个，
   *    他明明有权限却找不到入口。
   * ⚠️ **"能上自己的货"和"有自己的钱包"是两回事，别用同一个开关**：
   *    平台能上自营的货，但它的钱和店铺资料在 `/admin` 里。
   */
  const canListProducts =
    type === "platform" || type === "supplier" || type === "distributor";
  /** 有自己的钱包和店铺资料。⚠️ 平台不在里面（服务端 `anyMerchant` 也不放行）。 */
  const hasOwnShop = type === "supplier" || type === "dealer" || type === "distributor";

  const nav = (
    <div className="space-y-1">
      {/*
        🔴 **这里不放身份切换器。** 老板：「身份已经有地方切换了，这里不需要切换身份」。
           切身份在**顶栏账号卡**里（`components/desktop/account-menu.tsx`）——
           那是逛商城时随时够得着的地方，而侧栏只在账户这几页才有。
           两处都放的话，同一件事有两个入口、还得两边一起改文案，
           而且侧栏第一屏被三行"我是谁"占掉，真正要点的经营入口被挤下去。
        ⚠️ 顶栏那张卡是**唯一**的入口，删了它就没有切换的地方了，别一起删。
      */}
      {activeOrg ? (
        /* ══════ 经营者身份 ══════ */
        activeOrg.type === "supplier" ? (
          /*
            🔴 **供应商没有"店铺"这一层。**
               货就是他自己出的，不存在进货；他也不卖给消费者。
               他日常就两件事：看货 / 发货，其余重活在 `/supplier` 后台。
          */
          <>
            <SideNavGroup>经营</SideNavGroup>
            <SideNavItem
              to="/my-products"
              search={orgSearch}
              icon={PackageSearch}
              label="我的商品"
              hint="供货价"
              active={at("/my-products")}
            />
            <SideNavItem
              to="/ship"
              search={orgSearch}
              icon={Truck}
              label="要我发的货"
              active={at("/ship")}
            />
            <SideNavItem to="/supplier" icon={Monitor} label="供应商后台" />
          </>
        ) : (
          <>
            <SideNavGroup>经营</SideNavGroup>
            {canPrice && (
              <SideNavItem
                to="/invite-share"
                icon={QrCode}
                label="邀请客户"
                active={at("/invite-share")}
              />
            )}
            {canPrice && (
              <SideNavItem
                to="/my-pricing"
                search={orgSearch}
                icon={Tag}
                label="我的定价"
                active={at("/my-pricing")}
              />
            )}
            {/*
              🔴 **「我的商品」和「我的定价」是两件事，不能合。**
                 定价管的是上级供的货我卖多少；这一页管的是我自己上架的货。
              ⚠️ 平台也有这一页 —— 后台那边管的是**全站商品**（审核、下架、
                 定平台价），没有"我自己上架一件"的入口。
            */}
            {canListProducts && (
              <SideNavItem
                to="/my-products"
                search={orgSearch}
                icon={PackageSearch}
                label="我的商品"
                active={at("/my-products")}
              />
            )}
            {canPrice && (
              <SideNavItem
                to="/shop-categories"
                search={orgSearch}
                icon={LayoutGrid}
                label="店铺分类"
                active={at("/shop-categories")}
              />
            )}

            <SideNavGroup>订单</SideNavGroup>
            <SideNavItem
              to="/seller-orders"
              search={orgSearch}
              icon={ClipboardList}
              label="客户订单"
              active={at("/seller-orders")}
            />
            <SideNavItem
              to="/ship"
              search={orgSearch}
              icon={Truck}
              label="要我发的货"
              active={at("/ship")}
            />

            <SideNavGroup>客户与钱</SideNavGroup>
            {canRecruit && (
              <SideNavItem
                to="/team"
                search={orgSearch}
                icon={Users}
                label="团队与客户"
                active={at("/team")}
              />
            )}
            {canPrice && (
              <SideNavItem
                to="/coupons"
                search={orgSearch}
                icon={Ticket}
                label="优惠券"
                active={at("/coupons")}
              />
            )}
            {/*
              ⚠️ 钱和店铺资料这几项服务端只对 supplier / dealer / distributor 放行 ——
                 平台的账和店在 `/admin` 里，不走商家端这套接口。
            */}
            {hasOwnShop && (
              <>
                <SideNavItem
                  to="/earnings"
                  search={orgSearch}
                  icon={Wallet}
                  label="收益明细"
                  active={at("/earnings")}
                />
                <SideNavItem
                  to="/withdraw"
                  search={orgSearch}
                  icon={CreditCard}
                  label="提现"
                  active={at("/withdraw")}
                />

                <SideNavGroup>设置</SideNavGroup>
                <SideNavItem
                  to="/shop-settings"
                  search={orgSearch}
                  icon={Settings}
                  label="店铺设置"
                  active={at("/shop-settings")}
                />
              </>
            )}
          </>
        )
      ) : (
        /* ══════ 买家身份 ══════ */
        <>
          {/*
            🔴 **「我自己」就是"我这个人要买东西"。**
               价格按我的进货价算（陈总没有上级，他的成本就是供应商的供货价）——
               所以这条入口要摆在这儿，而不是挂在店铺菜单里叫「进货」。
            ⚠️ `/restock` 会把身份切回「我自己」+ 进货视角再跳商城；
               已经在「我自己」下面点它也是对的（幂等）。
          */}
          <SideNavGroup>买东西</SideNavGroup>
          {/* ⚠️ 不再标「按你的进货价」—— 商品页顶部那条横幅已经说过一次。 */}
          <SideNavItem to="/restock" icon={PackagePlus} label="去逛商城" active={at("/restock")} />

          <SideNavGroup>我的</SideNavGroup>
          <SideNavItem to="/me" icon={User} label="账户概览" active={at("/me")} />
          <SideNavItem to="/orders" icon={Package} label="我的订单" active={at("/orders")} />
          <SideNavItem to="/addresses" icon={MapPin} label="收货地址" active={at("/addresses")} />
          <SideNavItem to="/messages" icon={Bell} label="站内消息" active={at("/messages")} />
          {/* 🔴 这个平台上的账号是有钱的，登录设备管理是刚需不是锦上添花。 */}
          <SideNavItem
            to="/security"
            icon={ShieldCheck}
            label="账号安全"
            active={at("/security")}
          />
        </>
      )}
    </div>
  );

  return (
    <DesktopShell>
      <Crumbs
        items={[
          { label: "首页", to: "/" },
          { label: activeOrg ? activeOrg.name : "个人中心", to: "/me" },
          { label: crumb ?? title ?? "" },
        ].filter((i) => i.label)}
      />
      <SideLayout nav={nav}>
        {title ? <h1 className="mb-5 text-[22px] font-medium">{title}</h1> : null}
        {children}
      </SideLayout>
    </DesktopShell>
  );
}
